import type { FocusEvent } from 'react'
import type { AppTone } from './AppUi'
import { CheckboxDropdownOption } from './Form'
import { MultiSelectFilterSummary } from './MultiSelectFilterSummary'
import { cx } from './utils'

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z" />
    </svg>
  )
}

export function closeCheckboxDropdownOnBlur(e: FocusEvent<HTMLDetailsElement>) {
  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
    e.currentTarget.removeAttribute('open')
  }
}

export type CheckboxMultiSelectOption<T extends string> = {
  value: T
  label: string
  checked: boolean
  onToggle: () => void
  tone?: AppTone
}

type ControlSize = 'sm' | 'md'

const labelClass: Record<ControlSize, string> = {
  sm: 'mb-1 block text-xs font-medium text-zinc-500 dark:text-zinc-400',
  md: 'mb-2 block text-sm font-medium text-zinc-800 dark:text-zinc-200',
}

const triggerHeight: Record<ControlSize, string> = {
  sm: 'h-[38px]',
  md: 'min-h-[42px]',
}

export function CheckboxMultiSelectDropdown<T extends string>({
  label,
  labelId,
  labelSize = 'sm',
  controlSize = 'sm',
  className,
  selected,
  getLabel,
  getTone,
  emptyLabel,
  clearOption,
  options,
}: {
  label: string
  labelId: string
  labelSize?: ControlSize
  controlSize?: ControlSize
  className?: string
  selected: readonly T[]
  getLabel: (value: T) => string
  getTone?: (value: T) => AppTone | undefined
  emptyLabel: string
  clearOption: {
    label: string
    active: boolean
    onSelect: () => void
  }
  options: readonly CheckboxMultiSelectOption<T>[]
}) {
  return (
    <div className={className}>
      <label id={labelId} className={labelClass[labelSize]}>
        {label}
      </label>
      <details
        className="group relative z-40 [&>summary::-webkit-details-marker]:hidden"
        onBlur={closeCheckboxDropdownOnBlur}
      >
        <summary
          aria-labelledby={labelId}
          className={cx(
            'flex w-full cursor-pointer list-none items-center justify-between gap-2 rounded-xl border border-zinc-200/90 bg-white py-2 pl-3 pr-2 text-sm text-zinc-900 shadow-sm outline-none transition',
            triggerHeight[controlSize],
            'hover:border-violet-300/70 hover:bg-violet-50/25',
            'focus-visible:border-violet-500 focus-visible:ring-2 focus-visible:ring-violet-500/20',
            'group-open:border-violet-400/80 group-open:bg-violet-50/30 group-open:ring-2 group-open:ring-violet-500/15',
            'dark:border-zinc-700/90 dark:bg-zinc-900/90 dark:text-zinc-100',
            'dark:hover:border-violet-500/35 dark:hover:bg-violet-950/25',
            'dark:group-open:border-violet-500/50 dark:group-open:bg-violet-950/30 dark:group-open:ring-violet-500/20',
          )}
        >
          <MultiSelectFilterSummary
            selected={selected}
            getLabel={getLabel}
            getTone={getTone}
            emptyLabel={emptyLabel}
          />
          <span
            className={cx(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition duration-200',
              'bg-zinc-100/90 text-zinc-500',
              'group-open:rotate-180 group-open:bg-violet-100 group-open:text-violet-700',
              'dark:bg-zinc-800 dark:text-zinc-400',
              'dark:group-open:bg-violet-950/60 dark:group-open:text-violet-300',
            )}
            aria-hidden
          >
            <ChevronDownIcon className="h-4 w-4" />
          </span>
        </summary>

        <div
          className={cx(
            'absolute z-50 mt-1.5 min-w-full overflow-hidden rounded-2xl border border-zinc-200/80 p-1.5',
            'bg-white/95 shadow-[0_16px_48px_-16px_rgba(24,24,27,0.35)] ring-1 ring-zinc-950/5 backdrop-blur-md',
            'dark:border-zinc-600/70 dark:bg-zinc-900/95 dark:shadow-black/50 dark:ring-white/10',
          )}
        >
          <div className="max-h-72 space-y-0.5 overflow-y-auto overscroll-contain">
            <CheckboxDropdownOption
              variant="clear"
              checked={clearOption.active}
              onToggle={clearOption.onSelect}
            >
              {clearOption.label}
            </CheckboxDropdownOption>

            <div
              className="mx-2 my-1 border-t border-zinc-100 dark:border-zinc-800"
              role="separator"
            />

            {options.map((option) => (
              <CheckboxDropdownOption
                key={option.value}
                checked={option.checked}
                onToggle={option.onToggle}
                tone={option.tone}
              >
                {option.label}
              </CheckboxDropdownOption>
            ))}
          </div>
        </div>
      </details>
    </div>
  )
}
